import { ReactNode, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { Adg4Button } from "@/components/adg4/button/src";
import { ProductKey } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";

import { useSignupFormContext } from "../../../../signup-form/hooks";
import { CobrandedReturnToExistingProduct } from "../../return-to-existing-product/cobranded-return-to-existing-product";
import { Adg4SiteNameForm } from "../atlas-66195-cobranding/site-name-form";

type ContactExpandType = {
    productKeyForExpand: string;
};

export const CobrandedContactExpand = ({
    productKeyForExpand
}: ContactExpandType) => {
    const createNewWorkspace = useMemo(() => {
        if (typeof window === "undefined") return false;
        const queryParams = new URLSearchParams(window.location.search);
        return queryParams.get("createNew") === "true";
    }, []);

    const [showSiteNaming, setShowSiteNaming] = useState(createNewWorkspace);
    const { productName } = useSignupFormContext();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();

    const clickReturnToExistingWorkspace = () => {
        setShowSiteNaming(false);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "contactExpandGoToReturnToProductScreen",
            attributes: {
                productName
            }
        }).fire();
    };

    const goToExistingWorkspaceMessageValues = {
        link: (str: ReactNode) => (
            <Adg4Button
                css={goToExistingWorkspaceLinkCss}
                onClick={clickReturnToExistingWorkspace}
            >
                {str}
            </Adg4Button>
        )
    };

    const goToExistingWorkspaceMessage =
        productKeyForExpand === ProductKey.CONFLUENCE
            ? intl.formatMessage(
                  {
                      id: "BxpSignup.SiteExpansionForm.cobrandedGoToExistingWorkspace",
                      defaultMessage:
                          "or <link>visit an existing workspace</link>"
                  },
                  goToExistingWorkspaceMessageValues
              )
            : intl.formatMessage(
                  {
                      id: "bxpSignup.companyExpand.cobrandedReturnToJoinSites",
                      defaultMessage: "or <link>join an existing site</link>"
                  },
                  goToExistingWorkspaceMessageValues
              );

    return showSiteNaming ? (
        <div css={containerCss}>
            <Adg4SiteNameForm />
            <div css={goToExistingWorkspaceMessageCss}>
                {goToExistingWorkspaceMessage}
            </div>
        </div>
    ) : (
        <CobrandedReturnToExistingProduct
            productKeyForExpand={productKeyForExpand}
            exitView={() => {
                setShowSiteNaming(true);
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId: "contactExpandGoToSiteNamingScreen",
                    attributes: {
                        productName
                    }
                }).fire();
            }}
        />
    );
};

const containerCss = css({
    display: "grid",
    minHeight: "520px",
    alignContent: "space-between"
});

const goToExistingWorkspaceLinkCss = css({
    backgroundColor: "transparent !important",
    border: 0,
    borderRadius: 0,
    color: wacToken("color.primary.blue"),
    cursor: "pointer",
    fontSize: "inherit",
    fontWeight: "inherit",
    minHeight: "auto",
    minWidth: "auto",
    padding: "0 !important",
    textDecoration: "underline !important",
    [":hover"]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#1558BC !important"
    }
});

const goToExistingWorkspaceMessageCss = css({
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center"
});
